// Bounded-context helpers for filtering / highlighting the flow canvas
// and the features panel. Pure functions — no React, no DOM.
//
// The flow grammar treats bounded contexts as actors with kind='context'.
// Humans (kind='human') and external systems (kind='externalSystem') are
// NOT bounded contexts — they're upstream initiators. Two BCs are
// "related" if they participate in the same action chain.
//
// Chain definition: a chain is rooted at an action statement and walked
// forward via :: from / :: on / => references. Every statement reachable
// from that root is a chain member. Two BCs are related iff they appear
// as the actor of any statement in the same chain.

import type { ParsedFlow, Statement, TriggerExpr } from './flowModel';

export interface BoundedContextInfo {
  name: string;
  statementCount: number;
}

// Collect every distinct bounded-context name in the flow, sorted by
// statement count desc (then name asc as a stable tiebreaker). Used by
// the FlowNavBar dropdown so the largest BCs sort to the top.
export function getBoundedContexts(flow: ParsedFlow): BoundedContextInfo[] {
  const counts = new Map<string, number>();
  for (const s of flow.statements) {
    if (!s.actor || s.actor.kind !== 'context') continue;
    const name = s.actor.name;
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, statementCount]) => ({ name, statementCount }))
    .sort((a, b) => {
      if (b.statementCount !== a.statementCount) return b.statementCount - a.statementCount;
      return a.name.localeCompare(b.name);
    });
}

// Walk a trigger expression to extract event-id refs. Same shape as the
// helper in flowLayout but kept local so this module stays standalone.
function collectEventRefs(expr: TriggerExpr, out: number[]): void {
  if ('ref' in expr) out.push(expr.ref);
  else {
    collectEventRefs(expr.left, out);
    collectEventRefs(expr.right, out);
  }
}

// Build an UNDIRECTED adjacency map across the flow's reference edges:
//   • s :: from [a]                  → s ↔ a
//   • s :: on (event refs)           → s ↔ producer-of(event) for each ref
//   • s emits [e1, e2, ...]          → s ↔ e1, s ↔ e2, ...
// This lets us answer "what is connected to this BC's statements?" via
// a plain BFS, regardless of which side of the chain the BC sits on
// (originating actor, intermediate handler, downstream reaction).
export function buildAdjacency(flow: ParsedFlow): Map<number, Set<number>> {
  const adj = new Map<number, Set<number>>();
  const link = (a: number, b: number) => {
    if (a === b) return;
    if (!adj.has(a)) adj.set(a, new Set());
    if (!adj.has(b)) adj.set(b, new Set());
    adj.get(a)!.add(b);
    adj.get(b)!.add(a);
  };

  // producer-of: which statement emits each event id (first writer wins,
  // matching the flowLayout convention).
  const producerOf = new Map<number, number>();
  for (const s of flow.statements) {
    for (const e of s.emits) {
      if (!producerOf.has(e)) producerOf.set(e, s.id);
    }
  }

  for (const s of flow.statements) {
    if (s.triggeredBy?.kind === 'from') {
      link(s.id, s.triggeredBy.actionId);
    } else if (s.triggeredBy?.kind === 'on') {
      const refs: number[] = [];
      collectEventRefs(s.triggeredBy.expr, refs);
      for (const r of refs) {
        const prod = producerOf.get(r);
        if (prod !== undefined) link(s.id, prod);
        link(s.id, r); // also link to the event statement itself
      }
    }
    for (const e of s.emits) link(s.id, e);
  }

  return adj;
}

// BFS from a set of seed statement IDs across the undirected adjacency
// graph. Returns every reachable statement.
function reachableFrom(seeds: Iterable<number>, adj: Map<number, Set<number>>): Set<number> {
  const visited = new Set<number>();
  const queue: number[] = [];
  for (const s of seeds) {
    if (!visited.has(s)) { visited.add(s); queue.push(s); }
  }
  while (queue.length) {
    const cur = queue.shift()!;
    const nbrs = adj.get(cur);
    if (!nbrs) continue;
    for (const n of nbrs) {
      if (visited.has(n)) continue;
      visited.add(n);
      queue.push(n);
    }
  }
  return visited;
}

// Statement IDs that should be visible when a BC filter is active.
// Definition: every statement transitively connected to any statement
// owned by the primary BC, walked across the undirected reference
// graph (:: from / :: on / => edges). Includes the primary's own
// statements as seeds so a BC with only standalone commands still
// renders something.
export function computeFilteredStatementIds(flow: ParsedFlow, primary: string): Set<number> {
  if (!primary) return new Set(flow.statements.map((s) => s.id));
  const seeds: number[] = [];
  for (const s of flow.statements) {
    if (s.actor?.kind === 'context' && s.actor.name === primary) seeds.push(s.id);
  }
  if (seeds.length === 0) return new Set();
  const adj = buildAdjacency(flow);
  return reachableFrom(seeds, adj);
}

// For a primary BC, return the set of related BC names (excluding the
// primary itself). A BC is "related" iff one of its statements is
// reachable from any of the primary's statements via the same
// undirected walk used by computeFilteredStatementIds — same neighbour
// definition keeps the navbar's Related strip and the canvas content
// in sync.
export function computeRelatedBcs(flow: ParsedFlow, primary: string): Set<string> {
  if (!primary) return new Set();
  const visible = computeFilteredStatementIds(flow, primary);
  const byId = new Map<number, Statement>();
  flow.statements.forEach((s) => byId.set(s.id, s));

  const related = new Set<string>();
  for (const id of visible) {
    const s = byId.get(id);
    if (!s?.actor || s.actor.kind !== 'context') continue;
    if (s.actor.name === primary) continue;
    related.add(s.actor.name);
  }
  return related;
}

// Statement IDs that should be DIMMED (transitively linked to primary,
// but the statement itself is not owned by the primary BC). Used by
// the canvas to apply reduced opacity so the primary's own work pops.
export function computeDimmedStatementIds(flow: ParsedFlow, primary: string): Set<number> {
  if (!primary) return new Set();
  const visible = computeFilteredStatementIds(flow, primary);
  const byId = new Map<number, Statement>();
  flow.statements.forEach((s) => byId.set(s.id, s));

  const dimmed = new Set<number>();
  for (const id of visible) {
    const s = byId.get(id);
    if (!s) continue;
    if (s.actor?.kind === 'context' && s.actor.name === primary) continue;
    dimmed.add(id);
  }
  return dimmed;
}
